import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TrialGuard from './TrialGuard';
import {
  Search, PhoneIncoming, MessageSquare, PhoneOutgoing, MessageCircle,
  Calendar, Music, Settings, Users, FileText, Brain, BarChart3, Heart,
  Shield, LogOut, X, Menu, Bell, PanelLeftClose, PanelLeftOpen, LayoutDashboard, User, LifeBuoy
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem
} from './ui/dropdown-menu';

const NavItem = ({ icon: Icon, label, active, onClick, collapsed }) => (
  <button
    onClick={onClick}
    title={label}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group
      ${collapsed ? 'lg:justify-center lg:px-0' : ''}
      ${active
        ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'
        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
      }`}
  >
    <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 group-hover:text-gray-600 dark:hover:text-gray-300'}`} />
    <span className={`flex-1 text-left truncate ${collapsed ? 'lg:hidden' : ''}`}>{label}</span>
  </button>
);

const navSections = [
  {
    label: 'YOUR FRONT DESK',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
      { icon: Search, label: 'Ask Mia', path: '/business-setup' },
      { icon: PhoneIncoming, label: 'Inbound Calls', path: '/inbound-calls' },
      { icon: MessageSquare, label: 'Chat Agent', path: '/chat-agent' },
      { icon: PhoneOutgoing, label: 'Outbound Calls', path: '/outbound-calls' },
      { icon: MessageCircle, label: 'Tickets', path: '/tickets' },
      { icon: Calendar, label: 'Appointments', path: '/appointments' },
      { icon: Music, label: 'Hold Music', path: '/hold-music' },
      { icon: Settings, label: 'Settings', path: '/settings' },
    ]
  },
  {
    label: 'TOOLS',
    items: [
      { icon: Users, label: 'CRM', path: '/crm' },
      { icon: FileText, label: 'Invoices', path: '/invoices' },
      { icon: Brain, label: 'Insights', path: '/conversation-insights' },
      { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    ]
  },
  {
    label: 'ACCOUNT',
    items: [
      { icon: Users, label: 'Team', path: '/teams' },
      { icon: Heart, label: 'Affiliate Program', path: '/affiliate' },
      { icon: LifeBuoy, label: 'Help & Support', path: '/help-support' },
    ]
  }
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1');
  const toggleSidebarCollapsed = () => {
    const val = !sidebarCollapsed;
    setSidebarCollapsed(val);
    localStorage.setItem('sidebar_collapsed', val ? '1' : '0');
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const collapsed = sidebarCollapsed;

  return (
    <div className="flex h-screen bg-white dark:bg-[#0b1a30] overflow-hidden" data-testid="app-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ═══ LEFT SIDEBAR ═══ */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 ${collapsed ? 'lg:w-[68px]' : 'lg:w-60'} bg-[#fafbfc] dark:bg-[#0d1f35] border-r border-gray-200 dark:border-white/10 flex flex-col transition-[transform,width] duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        {/* Logo */}
        <div className={`py-4 border-b border-gray-100 dark:border-white/10 flex ${collapsed ? 'px-4 lg:px-2 lg:flex-col lg:items-center lg:gap-3 items-center justify-between' : 'px-4 items-center justify-between'}`}>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0" data-testid="miadesk-logo">
            <img src="/logo-mark.png" alt="Miadesk" className={`h-7 w-auto flex-shrink-0 ${collapsed ? 'lg:h-5' : ''}`} />
            <span className={`text-gray-900 dark:text-white font-semibold text-base ${collapsed ? 'lg:hidden' : ''}`}>Miadesk</span>
          </button>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={toggleSidebarCollapsed}
            className="hidden lg:block text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-4 space-y-6 ${collapsed ? 'px-3 lg:px-2.5' : 'px-3'}`}>
          {navSections.map((section, idx) => (
            <div key={section.label}>
              <p className={`px-3 mb-2 text-[10px] font-bold tracking-widest text-gray-400 uppercase ${collapsed ? 'lg:hidden' : ''}`}>{section.label}</p>
              {collapsed && idx > 0 && <div className="hidden lg:block h-px bg-gray-200 dark:bg-white/10 mb-3 mx-2" />}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItem
                    key={item.label}
                    icon={item.icon}
                    label={item.label}
                    collapsed={collapsed}
                    active={location.pathname === item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User Profile at bottom */}
        <div className={`border-t border-gray-100 dark:border-white/10 py-3 ${collapsed ? 'px-3 lg:px-2.5' : 'px-3'}`}>
          {user?.is_admin && (
            <NavItem icon={Shield} label="Admin Panel" collapsed={collapsed} onClick={() => navigate('/admin')} />
          )}
          <div className={`flex items-center gap-2.5 px-3 py-2 mt-1 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0" title={user?.email} data-testid="user-avatar">
              {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-testid="user-name">{user?.full_name || 'User'}</p>
              <p className="text-[11px] text-gray-400 truncate" data-testid="user-email">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} title="Log out" className={`flex items-center gap-2 w-full px-3 py-2 mt-1 rounded-lg text-sm text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors ${collapsed ? 'lg:justify-center lg:px-0' : ''}`} data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
            <span className={collapsed ? 'lg:hidden' : ''}>Log out</span>
          </button>
        </div>
      </aside>

      {/* ═══ MAIN AREA ═══ */}
      <div className="flex-1 flex flex-col min-w-0">
        <TrialGuard />
        {/* Top Bar */}
        <header className="h-14 border-b border-gray-200 dark:border-white/10 flex items-center justify-between px-4 lg:px-6 bg-white dark:bg-[#0b1a30] flex-shrink-0" data-testid="top-bar">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 mr-3"
            title="Open sidebar"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors" data-testid="notifications-btn">
              <Bell className="w-[18px] h-[18px]" />
            </button>
            <button onClick={() => navigate('/settings')} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors" data-testid="settings-top-btn">
              <Settings className="w-[18px] h-[18px]" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold ml-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="profile-menu-btn">
                  {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="profile-dropdown">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" data-testid="profile-dropdown-name">{user?.full_name || 'User'}</p>
                      <p className="text-xs text-muted-foreground font-normal truncate" data-testid="profile-dropdown-email">{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer" data-testid="profile-menu-account">
                  <User className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer" data-testid="profile-menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Account Settings
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer" data-testid="profile-menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="profile-menu-signout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="app-content flex-1 min-h-0 overflow-y-auto" data-testid="layout-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
